import React, { useState } from 'react';
import { ToggleButton, ToggleButtonGroup, CircularProgress, Typography, Box } from '@mui/material';
import ReportShell from '../components/ReportShell';
import PivotTable from '../components/PivotTable';
import YearOverlayChart from '../components/YearOverlayChart';
import SeriesToggle, { useToggleSet } from '../components/SeriesToggle';
import { MeasureControls, useMeasure } from '../components/MeasureControls';
import YearBasisToggle from '../components/YearBasisToggle';
import useStats from '../useStats';
import { fmtMoney, fmtInt } from '../lib/pivot';
import { GRANULARITIES, yoyMatrix, yearColour } from '../lib/periods';

const EMPTY = { years: [], indices: [], idxLabel: (x) => x, valueAt: () => 0 };

/**
 * Year-on-year master: pick a measure (revenue / orders / units), revenue basis
 * (net / +postage / postage), and granularity (week / month / quarter). Years
 * overlay on a line chart, with the same numbers in a period × year pivot below.
 * Years can be toggled on/off.
 */
export default function YearOnYear() {
  const { stats, loading } = useStats();
  const measure = useMeasure('revenue');
  const [gran, setGran] = useState('month');
  const [yearBasis, setYearBasis] = useState('calendar');

  const m = stats ? yoyMatrix(stats, gran, measure.get, yearBasis) : EMPTY;
  const yearToggle = useToggleSet(m.years);
  const latestYear = m.years.length ? m.years[m.years.length - 1] : undefined;
  const shownYears = m.years.filter((y) => yearToggle.selected.has(y));

  const controls = (
    <>
      <MeasureControls {...measure} />
      <ToggleButtonGroup size="small" exclusive value={gran} onChange={(_, v) => v && setGran(v)}>
        {Object.entries(GRANULARITIES).map(([k, g]) => (
          <ToggleButton key={k} value={k}>{g.label}</ToggleButton>
        ))}
      </ToggleButtonGroup>
      <YearBasisToggle value={yearBasis} onChange={setYearBasis} />
    </>
  );

  let body;
  if (loading || !stats) {
    body = <CircularProgress />;
  } else if (!m.years.length || !m.indices.length) {
    body = <Typography variant="body2" color="text.secondary">No data yet — import orders and Rebuild stats.</Typography>;
  } else {
    const rows = m.indices.map((i) => ({ key: String(i), label: m.idxLabel(i) }));
    const cols = shownYears.map((y) => ({ key: y, label: y }));
    body = (
      <>
        <Box sx={{ mb: 1 }}>
          <SeriesToggle
            label="Years"
            items={m.years.map((y) => ({ key: y, label: y, colour: yearColour(y, latestYear) }))}
            selected={yearToggle.selected}
            onToggle={yearToggle.toggle}
            onAll={yearToggle.all}
            onNone={yearToggle.none}
          />
        </Box>
        <YearOverlayChart years={shownYears} indices={m.indices} idxLabel={m.idxLabel} valueAt={m.valueAt} money={measure.money} latestYear={latestYear} />
        <Box sx={{ mt: 3 }}>
          {cols.length ? (
            <PivotTable
              rows={rows}
              cols={cols}
              value={(rk, ck) => m.valueAt(ck, Number(rk))}
              rowLabel={GRANULARITIES[gran].label}
              format={measure.money ? fmtMoney : fmtInt}
              sortByTotal={false}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">No years selected.</Typography>
          )}
        </Box>
      </>
    );
  }

  return <ReportShell title="Year on year" controls={controls}>{body}</ReportShell>;
}
